import React from 'react';
import {
  Modal,
  Icon,
  Input,
  Grid,
  Button,
  Radio,
  Select,
} from 'semantic-ui-react';
import { connect } from 'react-redux';
import _ from 'lodash';

import { TitledCard } from 'Components/';
import * as EditMenuSelectors from 'Pages/managemenu/state/editmenu/selectors';
import * as EditMenuActions from 'Pages/managemenu/state/editmenu/actions';
import { FIND_MODES, MESSAGES } from 'Pages/managemenu/helpers/Constants';
import * as DialogActions from 'Components/dialogs/state/actions';
import * as DialogSelectors from 'Components/dialogs/state/selectors';
import { RecipeFieldEnum as RecipeFields } from 'Common/FieldEnum';
import { NOTIFICATION_DIALOG_MESSAGES } from 'Common/Constants';
import { withRecipesSubscription } from 'Common/Components/RecipeSearch';
import { createToast, toastType } from 'Common/ToastHelper';
import { RelativePortal } from 'Common/Components';

import * as Utilities from '../utilities';
import { FindResultsTable } from './FindResultsTable';

function ReplaceRemoveModal(props) {
  const {
    modalProps,
    recipeList,
    setNotificationDialog,
    searchRecipes,
    loadingRecipes,
    recipeOptions,
    searchResults,
    recipesById,
    analyzeMenu,
    recipeCountByDayMap,
    showDialog,
  } = props;
  const onClose = modalProps.onClose || function () {};
  const [menuRecipeSearch, setMenuRecipeSearch] = React.useState('');
  const [foundRecipes, setFoundRecipes] = React.useState(recipeList);
  const [selectedRecipes, setSelectedRecipes] = React.useState({});
  const [mode, setMode] = React.useState(FIND_MODES.REMOVE);
  const [replaceWithRecipeId, setReplaceWithRecipeId] = React.useState();
  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectValue, setSelectValue] = React.useState('');

  const clearSelectedRecipes = () => setSelectedRecipes({});

  const onApplyClick = () => {
    const recipeIds = Object.keys(selectedRecipes);
    if (recipeIds.length === 0) {
      createToast(toastType.error, 'Error', 'No recipes were selected.');
      return;
    }

    if (mode === FIND_MODES.REMOVE) {
      setNotificationDialog({
        ...NOTIFICATION_DIALOG_MESSAGES.REMOVE_FOUND_RECIPES,
        actionsToDispatchOnOk: [EditMenuActions.deleteRecipes(recipeIds)],
        functionsToRunOnOk: [clearSelectedRecipes, analyzeMenu],
      });
    } else {
      if (!replaceWithRecipeId) {
        createToast(
          toastType.error,
          'Error',
          'No recipe was selected to replace recipes with.'
        );
        return;
      }

      const recipe = searchResults.find(
        (rec) => rec[RecipeFields.Id] === replaceWithRecipeId
      );

      if (recipe) {
        if (!Utilities.hasValidServingSize(recipe)) {
          createToast(
            toastType.error,
            'Error',
            MESSAGES.ADD_RECIPE_INVALID_SERVING_SIZE
          );
          return;
        }

        const daysWithDuplicates = Utilities.getReplaceRecipeDuplicateDays(
          recipeIds,
          recipesById,
          recipeCountByDayMap,
          recipe
        );

        if (daysWithDuplicates.length > 0) {
          setNotificationDialog({
            ...NOTIFICATION_DIALOG_MESSAGES.CANNOT_REPLACE_RECIPES_DUPLICATES,
            message: `${
              NOTIFICATION_DIALOG_MESSAGES.CANNOT_REPLACE_RECIPES_DUPLICATES
                .message
            }${daysWithDuplicates.map((d) => `Day ${d}`).join('\n')}`,
          });
          return;
        }

        const irreplaceableRecipes = Utilities.getIrreplaceableRecipes(
          recipesById,
          recipeIds,
          recipe
        );

        if (irreplaceableRecipes.length > 0) {
          setNotificationDialog({
            ...NOTIFICATION_DIALOG_MESSAGES.CANNOT_REPLACE_RECIPES,
            message: `${
              NOTIFICATION_DIALOG_MESSAGES.CANNOT_REPLACE_RECIPES.message
            } ${Utilities.getIrreplaceableRecipeMsg(irreplaceableRecipes)}`,
          });
          return;
        }

        setNotificationDialog({
          ...NOTIFICATION_DIALOG_MESSAGES.REPLACE_FOUND_RECIPES,
          actionsToDispatchOnOk: [
            EditMenuActions.replaceRecipes(recipeIds, recipe),
          ],
          functionsToRunOnOk: [clearSelectedRecipes, analyzeMenu],
        });
      }
    }
  };

  const debouncedSearch = React.useCallback(
    _.debounce((searchTerm) => {
      searchRecipes({ newSearch: true, Search: searchTerm });
    }, 125)
  );

  React.useEffect(() => {
    if (searchTerm) {
      debouncedSearch(searchTerm);
      setReplaceWithRecipeId(null);
    }
  }, [searchTerm]);

  // Update the list of recipes shown in the results if any have been updated by replace/remove
  React.useEffect(() => {
    if (menuRecipeSearch) {
      const recipes = Utilities.findRecipes(menuRecipeSearch, recipeList);
      setFoundRecipes(recipes);
    } else {
      setFoundRecipes(recipeList);
    }
  }, [recipeList]);

  const dropdown = (
    <Select
      fluid={true}
      placeholder="Search for recipe to replace with"
      options={recipeOptions}
      disabled={mode !== FIND_MODES.REPLACE}
      search={true}
      onSearchChange={(e, { searchQuery }) => setSearchTerm(searchQuery)}
      onChange={(e, { value }) => {
        setSelectValue(value);
        setReplaceWithRecipeId(value);
      }}
      loading={loadingRecipes}
      value={selectValue}
    />
  );

  return (
    <Modal {...modalProps}>
      <Modal.Header className="manage-menu-modal-header">
        Remove / Replace Recipes
        <Icon name="close" onClick={onClose} className="manage-menu-close" />
      </Modal.Header>
      <Modal.Content className="manage-menu-modal-content">
        <Grid>
          <Grid.Row>
            <Grid.Column width={6}>
              <label>Find</label>
              <Input
                fluid
                placeholder="Search for recipe"
                icon="search"
                value={menuRecipeSearch}
                onChange={(e) => {
                  setMenuRecipeSearch(e.target.value);
                  const recipes = Utilities.findRecipes(
                    e.target.value,
                    recipeList
                  );
                  setFoundRecipes(recipes);
                  clearSelectedRecipes();
                }}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <TitledCard title="Results" className="menu-find-results-tbl">
                <FindResultsTable
                  recipes={foundRecipes}
                  selectedRecipes={selectedRecipes}
                  setSelectedRecipes={setSelectedRecipes}
                />
              </TitledCard>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <label>With Selected Recipes:</label>
              <Radio
                label="Remove"
                value={FIND_MODES.REMOVE}
                checked={mode === FIND_MODES.REMOVE}
                onChange={(e, { value }) => setMode(value)}
              />
              <Radio
                label="Replace"
                value={FIND_MODES.REPLACE}
                checked={mode === FIND_MODES.REPLACE}
                onChange={(e, { value }) => setMode(value)}
                style={{ marginLeft: 16 }}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row style={{ marginTop: 8 }}>
            <Grid.Column width={6}>
              <label>Replace With:</label>
              {/* Use portal so that dropdown options aren't cutoff by semantic-ui modal but revert to just dropdown so options aren't
                  selectable when a dialog modal is open */}
              {showDialog ? (
                dropdown
              ) : (
                <RelativePortal fluid={true}>{dropdown}</RelativePortal>
              )}
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Modal.Content>
      <Modal.Actions>
        <div className="manage-menu-header-buttons">
          <Button className="mnm-button-blue" onClick={onClose}>
            Close
          </Button>
          <Button className="mnm-button-blue" onClick={onApplyClick}>
            Apply Changes
          </Button>
        </div>
      </Modal.Actions>
    </Modal>
  );
}

const mapStateToProps = (state, props) => ({
  recipeList: EditMenuSelectors.getRecipeList(state),
  recipeOptions: EditMenuSelectors.getRecipeOptions(state),
  recipesById: EditMenuSelectors.getRecipesById(state),
  recipeCountByDayMap: EditMenuSelectors.getRecipeCountByDayMap(state),
  showDialog: DialogSelectors.getShowDialog(state),
});

const mapDispatchToProps = {
  setNotificationDialog: DialogActions.setNotificationDialog,
};

const ConnectedComponent = withRecipesSubscription(
  connect(mapStateToProps, mapDispatchToProps)(ReplaceRemoveModal)
);

export { ConnectedComponent as ReplaceRemoveModal };



// WEBPACK FOOTER //
// ./src/pages/managemenu/subpages/editmenu/components/ReplaceRemoveModal.js